import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { StatusBadge, PriorityBadge } from "../components/Badges";
import { timeAgo } from "../lib/time";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Plus, Inbox, Ticket } from "lucide-react";

export default function MyTickets() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/tickets/mine")
      .then((r) => setTickets(r.data))
      .catch((e) => toast.error("Could not load your tickets", { description: formatApiError(e) }))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-display tracking-tight">
            My tickets
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Hi {user.name.split(" ")[0]} — here's everything you've submitted.
          </p>
        </div>
        <Link to="/submit">
          <Button data-testid="new-ticket-btn" className="bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-lg h-10">
            <Plus className="w-4 h-4 mr-1.5" /> New ticket
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="beacon-card p-10 text-center text-sm text-slate-500">Loading...</div>
      ) : tickets.length === 0 ? (
        <div className="beacon-card p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 grid place-items-center mx-auto">
            <Inbox className="w-7 h-7 text-slate-400" />
          </div>
          <div className="mt-3 font-semibold text-slate-800">No tickets yet</div>
          <div className="text-sm text-slate-500 mt-1">When you submit one, it'll show up here.</div>
          <Link to="/submit" className="inline-block mt-4">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg">
              <Plus className="w-4 h-4 mr-1.5" /> Submit your first ticket
            </Button>
          </Link>
        </div>
      ) : (
        <div className="beacon-card divide-y divide-slate-200">
          {tickets.map((t) => (
            <Link
              key={t.id}
              to={`/my-tickets/${t.id}`}
              data-testid={`my-ticket-${t.id}`}
              className="block p-4 sm:p-5 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Ticket className="w-3.5 h-3.5 text-slate-400" />
                    <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{t.ticket_number}</span>
                    <span className="text-[11px] text-slate-400">· {timeAgo(t.updated_at)}</span>
                  </div>
                  <div className="mt-1 font-semibold text-slate-900">{t.subject}</div>
                  <div className="text-xs text-slate-500 mt-0.5 capitalize">{t.category.replace("_", " ")}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <StatusBadge status={t.status} />
                  <PriorityBadge priority={t.priority} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
